import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './Toast';

const CATEGORIES = {
  all: { label: 'Toutes les échéances', icon: '📅' },
  tva: { label: 'TVA', icon: '📊' },
  social: { label: 'URSSAF & Social', icon: '👷' },
  impots: { label: 'Impôts & CFE', icon: '🏦' },
  juridique: { label: 'Juridique', icon: '⚖️' },
  autre: { label: 'Autres', icon: '📌' }
};

const EcheancesCalendar = ({ clientId }) => {
  const { addToast } = useToast();
  const [echeances, setEcheances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCustomModal, setShowCustomModal] = useState(false);

  // Formulaire personnalisée
  const [customForm, setCustomForm] = useState({
    titre: '',
    categorie: 'autre',
    date_echeance: '',
    description: '',
    montant_estime: ''
  });
  const [savingCustom, setSavingCustom] = useState(false);

  useEffect(() => {
    loadEcheances();
  }, [clientId]);

  const loadEcheances = async () => {
    setLoading(true);
    try {
      const targetId = clientId || '845364371';
      const resp = await api.get(`/echeances/${targetId}`);
      if (resp && (resp.success || resp.echeances)) {
        setEcheances(resp.echeances || []);
      } else if (Array.isArray(resp)) {
        setEcheances(resp);
      } else {
        console.warn('Echeances response empty/unexpected:', resp);
        setEcheances([]);
      }
    } catch (err) {
      console.error('Erreur echeances:', err);
      // Ne pas bloquer l'UI avec une fausse alerte si l'API renvoie un format valide
      setEcheances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustom = async (e) => {
    e.preventDefault();
    if (!customForm.titre || !customForm.date_echeance) {
      addToast('⚠️ Veuillez remplir le titre et la date d\'échéance', 'error');
      return;
    }

    setSavingCustom(true);
    try {
      const resp = await api.post(`/echeances/${clientId}/custom`, {
        ...customForm,
        montant_estime: parseFloat(customForm.montant_estime) || 0.0
      });
      if (resp.success) {
        addToast('✅ Rappel d\'échéance ajouté !', 'success');
        setShowCustomModal(false);
        setCustomForm({ titre: '', categorie: 'autre', date_echeance: '', description: '', montant_estime: '' });
        loadEcheances();
      } else {
        addToast('❌ Erreur lors de l\'enregistrement', 'error');
      }
    } catch (err) {
      console.error(err);
      addToast('❌ Erreur de connexion', 'error');
    } finally {
      setSavingCustom(false);
    }
  };

  const filteredEcheances = activeCategory === 'all'
    ? echeances
    : echeances.filter(item => item.categorie === activeCategory);

  const formatEuro = (val) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
  };

  const formatDateFr = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', background: '#F8FAFC', padding: '24px', minHeight: '100vh', boxSizing: 'border-box' }}>
      
      {/* Header & Title */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0F172A', letterSpacing: '-0.5px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>📅</span> Calendrier des Échéances & Rappels
          </h1>
          <p style={{ fontSize: '14px', color: '#64748B', marginTop: '4px', margin: '4px 0 0' }}>
            Suivi automatique des dates fiscales, sociales et juridiques pour ne manquer aucune échéance.
          </p>
        </div>

        <button
          onClick={() => setShowCustomModal(true)}
          style={{
            padding: '10px 18px', background: 'linear-gradient(135deg, #1D9E75, #10B981)',
            color: 'white', border: 'none', borderRadius: '10px', fontSize: '13.5px',
            fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
            boxShadow: '0 4px 12px rgba(29, 158, 117, 0.15)', transition: 'transform 0.2s'
          }}
        >
          <span>➕</span> Ajouter un rappel personnalisé
        </button>
      </div>

      {/* Categories Filter Tabs */}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '20px' }}>
        {Object.keys(CATEGORIES).map(catKey => {
          const cat = CATEGORIES[catKey];
          const isActive = activeCategory === catKey;
          const count = catKey === 'all' ? echeances.length : echeances.filter(i => i.categorie === catKey).length;

          return (
            <button
              key={catKey}
              onClick={() => setActiveCategory(catKey)}
              style={{
                padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600',
                border: isActive ? '2px solid #1D9E75' : '1px solid #E2E8F0',
                background: isActive ? '#E1F5EE' : 'white',
                color: isActive ? '#0F6E56' : '#475569',
                cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              <span>{cat.icon}</span>
              {cat.label}
              <span style={{
                background: isActive ? '#1D9E75' : '#F1F5F9',
                color: isActive ? 'white' : '#64748B',
                fontSize: '11px', padding: '2px 7px', borderRadius: '10px'
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Main Content List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', background: 'white', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
          <div className="animate-spin" style={{ width: '36px', height: '36px', border: '3px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }}></div>
          <p style={{ color: '#64748B', fontSize: '14px' }}>Génération de votre calendrier d'échéances...</p>
        </div>
      ) : filteredEcheances.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
          <span style={{ fontSize: '40px' }}>🎉</span>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A', marginTop: '12px' }}>Aucune échéance à venir dans cette catégorie</h3>
          <p style={{ fontSize: '13.5px', color: '#64748B', maxWidth: '400px', margin: '6px auto 0' }}>
            Toutes vos obligations sont à jour pour le moment.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {filteredEcheances.map((item, idx) => {
            const isCritique = item.statut_urgence === 'critique';
            const isProche = item.statut_urgence === 'proche';

            const badgeBg = isCritique ? '#FEE2E2' : (isProche ? '#FEF3C7' : '#E1F5EE');
            const badgeColor = isCritique ? '#991B1B' : (isProche ? '#92400E' : '#065F46');
            const badgeBorder = isCritique ? '#FCA5A5' : (isProche ? '#FDE68A' : '#A7F3D0');

            const daysText = item.jours_restants === 0
              ? 'Aujourd\'hui !'
              : (item.jours_restants > 0 ? `Dans ${item.jours_restants} jour${item.jours_restants > 1 ? 's' : ''}` : `Dépassé (${Math.abs(item.jours_restants)}j)`);

            return (
              <div
                key={item.id || idx}
                style={{
                  background: 'white', border: `1px solid ${isCritique ? '#FCA5A5' : '#E2E8F0'}`,
                  borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column',
                  justifyContent: 'space-between', gap: '14px', transition: 'all 0.2s',
                  boxShadow: isCritique ? '0 4px 12px rgba(239, 68, 68, 0.08)' : '0 2px 4px rgba(0,0,0,0.02)'
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: '#64748B', letterSpacing: '0.05em' }}>
                      {CATEGORIES[item.categorie]?.icon || '📌'} {CATEGORIES[item.categorie]?.label || item.categorie}
                    </span>

                    <span style={{
                      background: badgeBg, color: badgeColor, border: `1px solid ${badgeBorder}`,
                      fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '12px'
                    }}>
                      {daysText}
                    </span>
                  </div>

                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0F172A', margin: 0, lineHeight: '1.3' }}>
                    {item.titre}
                  </h3>

                  <p style={{ fontSize: '13px', color: '#475569', marginTop: '8px', lineHeight: '1.4', margin: '8px 0 0' }}>
                    {item.description}
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #F1F5F9', paddingTop: '12px', marginTop: 'auto' }}>
                  <div style={{ fontSize: '12px', color: '#64748B' }}>
                    Date limite : <strong style={{ color: '#0F172A' }}>{formatDateFr(item.date_echeance)}</strong>
                  </div>

                  {item.montant_estime > 0 && (
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#1D9E75' }}>
                      {formatEuro(item.montant_estime)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal d'ajout de rappel personnalisé */}
      {showCustomModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
        }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '28px', maxWidth: '460px', width: '100%', border: '1px solid #E2E8F0', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0, marginBottom: '6px' }}>
              Ajouter un rappel personnalisé
            </h2>
            <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 20px' }}>
              Configurez une échéance spécifique pour votre entreprise (ex: facture client, échéance prêt...).
            </p>

            <form onSubmit={handleCreateCustom} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Titre du rappel *</label>
                <input
                  type="text"
                  placeholder="Ex: Échéance facture client Dupont"
                  value={customForm.titre}
                  onChange={(e) => setCustomForm({ ...customForm, titre: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Catégorie</label>
                  <select
                    value={customForm.categorie}
                    onChange={(e) => setCustomForm({ ...customForm, categorie: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box', background: 'white' }}
                  >
                    <option value="tva">📊 TVA</option>
                    <option value="social">👷 URSSAF & Social</option>
                    <option value="impots">🏦 Impôts</option>
                    <option value="juridique">⚖️ Juridique</option>
                    <option value="autre">📌 Autre</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Date d'échéance *</label>
                  <input
                    type="date"
                    value={customForm.date_echeance}
                    onChange={(e) => setCustomForm({ ...customForm, date_echeance: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '4px' }}>Description / Note</label>
                <textarea
                  rows="3"
                  placeholder="Notes complémentaires..."
                  value={customForm.description}
                  onChange={(e) => setCustomForm({ ...customForm, description: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowCustomModal(false)}
                  style={{ padding: '10px 16px', background: '#F1F5F9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={savingCustom}
                  style={{ padding: '10px 20px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  {savingCustom ? 'Enregistrement...' : 'Valider'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default EcheancesCalendar;
